/**
 * API Security Layer
 *
 * Provides comprehensive security for API calls including:
 * - Request signing with HMAC-SHA256
 * - Payload obfuscation
 * - Browser fingerprinting
 * - Timestamp validation
 * - Nonce generation for replay attack prevention
 */

// ============================================================================
// Types
// ============================================================================

interface FingerprintData {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory: number | undefined;
  touchSupport: boolean;
  webglVendor: string;
  webglRenderer: string;
  canvasFingerprint: string;
}

// ============================================================================
// Constants
// ============================================================================

// XOR cipher key derived from multiple sources
const XOR_KEY_BASE = "hopp-secure-2024";

// Public endpoints that bypass security
const PUBLIC_ENDPOINTS = [
  "/health",
  "/api/health",
  "/public",
];

// Security version for backwards compatibility
const SECURITY_VERSION = "1.0";

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert ArrayBuffer to hex string
 */
function bufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Convert string to Uint8Array for Web Crypto API
 */
function stringToBuffer(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * Check if an endpoint is public and doesn't need security headers
 */
function isPublicEndpoint(path: string): boolean {
  const normalizedPath = path.toLowerCase().replace(/\/+/g, "/");
  return PUBLIC_ENDPOINTS.some(
    (endpoint) =>
      normalizedPath === endpoint ||
      normalizedPath.startsWith(endpoint + "/") ||
      normalizedPath.startsWith(endpoint + "?")
  );
}

// ============================================================================
// Cryptographic Key Derivation
// ============================================================================

/**
 * Derive a cryptographic key from browser properties
 * This creates a consistent key per browser/device combination
 */
async function deriveClientKey(): Promise<CryptoKey> {
  // Collect browser properties for key derivation
  // IMPORTANT: These values must match EXACTLY what is sent in headers to the backend
  const keyMaterial: string[] = [
    XOR_KEY_BASE,
    typeof navigator !== "undefined" ? navigator.userAgent : "ssr",
    typeof navigator !== "undefined" ? navigator.language : "en",
    typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : "0x0",
    typeof navigator !== "undefined" ? navigator.platform || "unknown" : "ssr",
    new Date().getTimezoneOffset().toString(),
  ];

  const keyString = keyMaterial.join("|");
  const keyData = stringToBuffer(keyString);

  // Create a hash of the key material
  const hashBuffer = await crypto.subtle.digest("SHA-256", keyData as BufferSource);

  // Import as HMAC key
  return crypto.subtle.importKey(
    "raw",
    hashBuffer,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"]
  );
}

/**
 * Generate XOR key from browser properties
 */
function generateXorKey(): string {
  const components: string[] = [
    XOR_KEY_BASE,
    typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 20) : "ssr",
    typeof screen !== "undefined" ? screen.colorDepth.toString() : "24",
  ];
  return components.join("-");
}

// ============================================================================
// Nonce Generation
// ============================================================================

/**
 * Generate a unique nonce for each request
 * Combines timestamp, random bytes, and counter for uniqueness
 */
let nonceCounter = 0;

function generateNonce(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  const counter = (++nonceCounter).toString(36);

  // Add random bytes if crypto is available
  let randomBytes = "";
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);
    randomBytes = Array.from(array)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  return `${timestamp}-${random}-${counter}-${randomBytes}`;
}

// ============================================================================
// Request Signing
// ============================================================================

/**
 * Generate HMAC-SHA256 signature for request
 *
 * @param path - API endpoint path
 * @param payload - Request payload (stringified)
 * @param timestamp - Unix timestamp in milliseconds
 * @param nonce - Unique nonce for this request
 * @returns Hex-encoded signature string
 */
async function generateRequestSignature(
  path: string,
  payload: string,
  timestamp: number,
  nonce: string
): Promise<string> {
  // Create signature data: path + timestamp + nonce + payload hash
  const payloadHash = await crypto.subtle.digest(
    "SHA-256",
    stringToBuffer(payload || "") as BufferSource
  );
  const payloadHashHex = bufferToHex(payloadHash);

  const signatureData = `${path}:${timestamp}:${nonce}:${payloadHashHex}`;

  // Get the derived client key
  const key = await deriveClientKey();

  // Sign the data
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    stringToBuffer(signatureData) as BufferSource
  );

  // Debug logging for signature generation
  return bufferToHex(signature);
}

// ============================================================================
// Payload Obfuscation
// ============================================================================

/**
 * XOR cipher implementation
 */
function xorCipher(input: string, key: string): string {
  let result = "";
  for (let i = 0; i < input.length; i++) {
    const charCode = input.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    result += String.fromCharCode(charCode);
  }
  return result;
}

/**
 * Obfuscate request payload
 * Applies XOR cipher then Base64 encoding
 *
 * @param data - Object to obfuscate
 * @returns Obfuscated string
 */
export function obfuscatePayload(data: object): string {
  // Stringify the data
  const jsonString = JSON.stringify(data);

  // Apply XOR cipher
  const xorKey = generateXorKey();
  const xored = xorCipher(jsonString, xorKey);

  // Base64 encode
  const encoded = btoa(
    encodeURIComponent(xored).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  );

  // Add a version prefix for backwards compatibility
  return `v1:${encoded}`;
}

/**
 * Build an explicit XOR key from the given UA slice and color depth.
 * Falls back to browser properties if not provided.
 */
function buildXorKey(ua?: string | null, cd?: string | null): string {
  if (ua != null && cd != null) {
    return [XOR_KEY_BASE, ua, cd].join("-");
  }
  return generateXorKey();
}

/**
 * Deobfuscate response payload
 * Reverses the obfuscation process
 *
 * @param data - Obfuscated string
 * @param xorUa - Optional explicit UA slice (first 20 chars) used by backend for obfuscation
 * @param xorCd - Optional explicit color depth string used by backend for obfuscation
 * @returns Original object
 */
export function deobfuscatePayload(
  data: string,
  xorUa?: string | null,
  xorCd?: string | null,
): object {
  // Check for version prefix
  let encoded = data;
  if (data.startsWith("v1:")) {
    encoded = data.substring(3);
  }

  // Base64 decode
  const decoded = decodeURIComponent(
    Array.from(atob(encoded))
      .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
      .join("")
  );

  // Apply XOR cipher (same operation reverses it)
  const xorKey = buildXorKey(xorUa, xorCd);
  const jsonString = xorCipher(decoded, xorKey);

  // Parse JSON
  return JSON.parse(jsonString);
}

// ============================================================================
// Browser Fingerprinting
// ============================================================================

/**
 * Get WebGL renderer info
 */
function getWebGLInfo(): { vendor: string; renderer: string } {
  const defaultInfo = { vendor: "unknown", renderer: "unknown" };

  if (typeof document === "undefined") {
    return defaultInfo;
  }

  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

    if (!gl || !(gl instanceof WebGLRenderingContext)) {
      return defaultInfo;
    }

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) {
      return defaultInfo;
    }

    return {
      vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || "unknown",
      renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "unknown",
    };
  } catch {
    return defaultInfo;
  }
}

/**
 * Generate canvas fingerprint
 */
function getCanvasFingerprint(): string {
  if (typeof document === "undefined") {
    return "ssr";
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return "no-context";
    }

    // Draw some text and shapes
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("hopp-fp", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("hopp-fp", 4, 17);

    // Get data URL and hash it
    return canvas.toDataURL().slice(-50);
  } catch {
    return "error";
  }
}

/**
 * Collect fingerprint data from browser
 */
function collectFingerprintData(): FingerprintData {
  const webglInfo = getWebGLInfo();

  return {
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "ssr",
    language: typeof navigator !== "undefined" ? navigator.language : "en",
    platform:
      typeof navigator !== "undefined" ? navigator.platform || "unknown" : "ssr",
    screenResolution:
      typeof screen !== "undefined"
        ? `${screen.width}x${screen.height}x${screen.availWidth}x${screen.availHeight}`
        : "0x0x0x0",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",
    colorDepth: typeof screen !== "undefined" ? screen.colorDepth : 24,
    hardwareConcurrency:
      typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 0 : 0,
    deviceMemory:
      typeof navigator !== "undefined"
        ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory
        : undefined,
    touchSupport:
      typeof navigator !== "undefined"
        ? "maxTouchPoints" in navigator && navigator.maxTouchPoints > 0
        : false,
    webglVendor: webglInfo.vendor,
    webglRenderer: webglInfo.renderer,
    canvasFingerprint: getCanvasFingerprint(),
  };
}

/**
 * Generate a unique browser/device fingerprint hash
 *
 * @returns SHA-256 hash of fingerprint data
 */
async function generateFingerprint(): Promise<string> {
  const data = collectFingerprintData();

  // Create a stable string representation
  const fingerprintString = [
    data.userAgent,
    data.language,
    data.platform,
    data.screenResolution,
    data.timezone,
    data.colorDepth,
    data.hardwareConcurrency,
    data.deviceMemory || 0,
    data.touchSupport,
    data.webglVendor,
    data.webglRenderer,
    data.canvasFingerprint,
  ].join("|");

  // Hash the fingerprint
  const hashBuffer = await crypto.subtle.digest(
    "SHA-256",
    stringToBuffer(fingerprintString) as BufferSource
  );

  return bufferToHex(hashBuffer);
}

// ============================================================================
// Secure Headers Generation
// ============================================================================

// Cache fingerprint to avoid recalculating
let cachedFingerprint: string | null = null;
let fingerprintPromise: Promise<string> | null = null;

const SESSION_STORAGE_KEY = "hopp_fp";

/**
 * Try to read fingerprint from sessionStorage (survives navigations within
 * the same tab, cleared when the tab is closed).
 */
function readStoredFingerprint(): string | null {
  try {
    if (typeof sessionStorage !== "undefined") {
      return sessionStorage.getItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // sessionStorage may be unavailable (SSR, incognito in some browsers, etc.)
  }
  return null;
}

/**
 * Persist fingerprint to sessionStorage so it survives page navigations.
 */
function storeFingerprint(fp: string): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(SESSION_STORAGE_KEY, fp);
    }
  } catch {
    // Silently ignore — storage quota exceeded, SSR, or restricted context
  }
}

/**
 * Get cached or generate new fingerprint
 */
async function getCachedFingerprint(): Promise<string> {
  if (cachedFingerprint) {
    return cachedFingerprint;
  }

  // Check sessionStorage before doing expensive WebGL/canvas work
  const stored = readStoredFingerprint();
  if (stored) {
    cachedFingerprint = stored;
    return stored;
  }

  if (fingerprintPromise) {
    return fingerprintPromise;
  }

  fingerprintPromise = generateFingerprint().then((fp) => {
    cachedFingerprint = fp;
    storeFingerprint(fp);
    fingerprintPromise = null;
    return fp;
  });

  return fingerprintPromise;
}

/**
 * Create all security headers for an API request
 *
 * @param path - API endpoint path
 * @param body - Optional request body (already stringified)
 * @returns Object containing all security headers
 */
export async function createSecureHeaders(
  path: string,
  body?: string
): Promise<Record<string, string>> {
  // Skip security for public endpoints
  if (isPublicEndpoint(path)) {
    return {
      "X-Request-Version": SECURITY_VERSION,
    };
  }

  const timestamp = Date.now();
  const nonce = generateNonce();
  const payload = body || "";

  // Generate all security components in parallel
  const [signature, fingerprint] = await Promise.all([
    generateRequestSignature(path, payload, timestamp, nonce),
    getCachedFingerprint(),
  ]);

  // Include key derivation properties so backend can derive the same HMAC key
  // These MUST match what deriveClientKey() uses for key material
  const screenResolution = typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : "0x0";
  const platform = typeof navigator !== "undefined" ? navigator.platform || "unknown" : "ssr";
  const timezoneOffset = new Date().getTimezoneOffset().toString();
  const language = typeof navigator !== "undefined" ? navigator.language : "en";
  // Color depth is used for XOR key derivation (generateXorKey)
  const colorDepth = typeof screen !== "undefined" ? screen.colorDepth.toString() : "24";

  return {
    "X-Request-Signature": signature,
    "X-Request-Timestamp": timestamp.toString(),
    "X-Request-Nonce": nonce,
    "X-Client-Fingerprint": fingerprint,
    "X-Request-Version": SECURITY_VERSION,
    // Key derivation properties for backend signature validation
    "X-Client-Screen": screenResolution,
    "X-Client-Platform": platform,
    "X-Client-Timezone": timezoneOffset,
    "X-Client-Language": language,
    // XOR key derivation property for obfuscation
    "X-Color-Depth": colorDepth,
  };
}

